/**
 * Tamil Data translation engine
 * =============================
 * A small, dependency-free i18n layer built for this site so every string
 * stays editable in plain JavaScript files under src/i18n/messages/.
 *
 * Design notes
 * ------------
 * - English is bundled eagerly because it is the fallback for every other
 *   locale. All other catalogues are code-split and fetched on demand, so
 *   visitors only download the language they actually use.
 * - Any key missing from the active locale falls back to English, then to the
 *   key itself. A partially translated catalogue therefore renders a usable
 *   page instead of blanks.
 * - A chunk of the site's copy lives in public/index.html, outside the Vue
 *   root (footer, why-us, stats, testimonials). `applyStaticTranslations`
 *   handles those nodes via `data-i18n*` attributes.
 *
 * Usage in templates:   {{ $t('nav.home') }}
 * Usage for lists:      $tm('fund.areas')      // returns the raw array
 * Usage in script:      import { t, setLocale, state } from '@/i18n'
 */

import { reactive, computed, readonly } from "vue";
import en from "./messages/en";
import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  getLocale,
  isSupported,
  resolveBrowserLocale,
} from "./locales";

const STORAGE_KEY = "tamildata.locale";

/** Catalogues that have been loaded so far, keyed by locale code. */
const catalogues = reactive({ [FALLBACK_LOCALE]: en });

const state = reactive({
  locale: DEFAULT_LOCALE,
  /** True while a catalogue is being fetched, for switcher spinners. */
  loading: false,
});

/** Subscribers notified after every successful locale change. */
const listeners = new Set();

/** Keys already reported as missing, so the console stays readable. */
const reportedMissing = new Set();

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/**
 * Walks a dot-separated path through a nested object.
 * Array indices work too: "fund.areas.0.title".
 */
function resolvePath(source, path) {
  if (!source) return undefined;

  let current = source;
  for (const segment of path.split(".")) {
    if (current === null || typeof current !== "object") return undefined;
    current = current[segment];
    if (current === undefined) return undefined;
  }
  return current;
}

function interpolate(template, params) {
  if (!params) return template;

  return template.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(params, name)
      ? String(params[name])
      : match
  );
}

function warnMissing(key) {
  if (process.env.NODE_ENV === "production") return;
  const id = `${state.locale}:${key}`;
  if (reportedMissing.has(id)) return;
  reportedMissing.add(id);
  // eslint-disable-next-line no-console
  console.warn(
    `[i18n] Missing "${key}" for locale "${state.locale}". ` +
      `Add it to src/i18n/messages/${state.locale}.js`
  );
}

/**
 * Raw lookup with fallback. Returns whatever the catalogue holds, which may
 * be a string, an array, or a nested object.
 */
function lookup(key) {
  const active = resolvePath(catalogues[state.locale], key);
  if (active !== undefined) return active;

  if (state.locale !== FALLBACK_LOCALE) {
    const fallback = resolvePath(catalogues[FALLBACK_LOCALE], key);
    if (fallback !== undefined) {
      warnMissing(key);
      return fallback;
    }
  }

  warnMissing(key);
  return undefined;
}

/**
 * Translate a key to a string.
 * @param {string} key dot-separated path, e.g. "nav.archive"
 * @param {Object} [params] values for {placeholders} in the message
 * @returns {string} the translated string, or the key if nothing was found
 */
export function t(key, params) {
  const value = lookup(key);

  if (typeof value === "string") return interpolate(value, params);
  if (value === undefined) return key;

  // Asking for an object or array via t() is a mistake worth surfacing.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] "${key}" is not a string. Use $tm() for lists.`);
  }
  return key;
}

/**
 * Translate a key to a list or object, for data-driven sections.
 * Always returns an array when `fallbackToArray` is left on, so templates can
 * v-for over the result without null checks.
 * @param {string} key dot-separated path
 * @returns {Array|Object} the raw catalogue value
 */
export function tm(key) {
  const value = lookup(key);
  if (value === undefined) return [];
  return value;
}

/** True if the key exists in the active locale or the fallback. */
export function te(key) {
  return lookup(key) !== undefined;
}

// ---------------------------------------------------------------------------
// Static DOM outside the Vue root
// ---------------------------------------------------------------------------

/** Attributes that can be translated, mapped from their data-* counterpart. */
const TRANSLATABLE_ATTRS = [
  ["data-i18n-alt", "alt"],
  ["data-i18n-title", "title"],
  ["data-i18n-aria-label", "aria-label"],
  ["data-i18n-placeholder", "placeholder"],
  ["data-i18n-content", "content"],
];

/**
 * Applies translations to markup that Vue does not own — the footer and the
 * marketing sections in public/index.html.
 *
 * Only `textContent` is written, never `innerHTML`, so a catalogue entry can
 * never inject markup.
 *
 * @param {ParentNode} [root=document] scope to search
 */
export function applyStaticTranslations(root = document) {
  if (typeof document === "undefined") return;

  root.querySelectorAll("[data-i18n]").forEach((el) => {
    const key = el.getAttribute("data-i18n");
    if (!key) return;
    const value = lookup(key);
    if (typeof value === "string") {
      el.textContent = interpolate(value, { year: new Date().getFullYear() });
    }
  });

  TRANSLATABLE_ATTRS.forEach(([dataAttr, targetAttr]) => {
    root.querySelectorAll(`[${dataAttr}]`).forEach((el) => {
      const key = el.getAttribute(dataAttr);
      if (!key) return;
      const value = lookup(key);
      if (typeof value === "string") el.setAttribute(targetAttr, value);
    });
  });
}

// ---------------------------------------------------------------------------
// Document-level side effects
// ---------------------------------------------------------------------------

/**
 * Every supported locale is currently left-to-right, so `dir` is always "ltr".
 * The attribute is still written from the registry rather than hardcoded, so
 * adding an RTL language later only needs `dir: "rtl"` on its entry plus
 * matching stylesheet work.
 */
function applyDocumentAttributes(code) {
  if (typeof document === "undefined") return;
  const locale = getLocale(code);
  const html = document.documentElement;

  html.setAttribute("lang", locale.htmlLang || locale.code);
  html.setAttribute("dir", locale.dir || "ltr");
  // Lets stylesheets target a specific language without JS.
  html.setAttribute("data-locale", locale.code);
}

// ---------------------------------------------------------------------------
// Loading and switching
// ---------------------------------------------------------------------------

/**
 * Fetches a catalogue if it is not already in memory.
 * The webpack magic comment keeps each locale in its own chunk.
 */
async function loadCatalogue(code) {
  if (catalogues[code]) return catalogues[code];

  try {
    const module = await import(
      /* webpackChunkName: "locale-[request]" */ `./messages/${code}.js`
    );
    catalogues[code] = module.default;
    return catalogues[code];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `[i18n] Could not load catalogue for "${code}", staying on ` +
        `"${state.locale}".`,
      error
    );
    return null;
  }
}

/**
 * Switches the active locale, loading its catalogue first.
 * Safe to call with an unsupported code — it resolves to the default.
 *
 * @param {string} code locale code, e.g. "de"
 * @param {Object} [options]
 * @param {boolean} [options.persist=true] remember the choice for next visit
 * @returns {Promise<string>} the locale that ended up active
 */
export async function setLocale(code, { persist = true } = {}) {
  const target = isSupported(code) ? code : DEFAULT_LOCALE;

  if (target === state.locale && catalogues[target]) {
    return state.locale;
  }

  state.loading = true;
  const loaded = await loadCatalogue(target);
  state.loading = false;

  if (!loaded) return state.locale;

  state.locale = target;
  applyDocumentAttributes(target);
  applyStaticTranslations();

  if (persist) {
    try {
      window.localStorage.setItem(STORAGE_KEY, target);
    } catch {
      // Private browsing or blocked storage — the choice just won't persist.
    }
  }

  listeners.forEach((fn) => {
    try {
      fn(target);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[i18n] locale change listener failed", error);
    }
  });

  return state.locale;
}

/**
 * Registers a callback fired after each locale change.
 * @returns {Function} unsubscribe
 */
export function onLocaleChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Works out which locale to start in:
 *   stored choice  ->  browser language  ->  default
 */
export function detectInitialLocale() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isSupported(stored)) return stored;
  } catch {
    // Ignore and fall through to browser detection.
  }

  if (typeof navigator !== "undefined") {
    const fromBrowser = resolveBrowserLocale(
      navigator.languages || [navigator.language]
    );
    if (fromBrowser) return fromBrowser;
  }

  return DEFAULT_LOCALE;
}

// ---------------------------------------------------------------------------
// Vue plugin
// ---------------------------------------------------------------------------

export const i18n = {
  install(app) {
    app.config.globalProperties.$t = t;
    app.config.globalProperties.$tm = tm;
    app.config.globalProperties.$te = te;
    app.config.globalProperties.$i18n = readonly(state);

    app.provide("i18n", {
      state: readonly(state),
      t,
      tm,
      te,
      setLocale,
    });

    // Boot into the visitor's language, then translate the static markup that
    // was already rendered by index.html.
    const initial = detectInitialLocale();
    applyDocumentAttributes(initial);

    if (initial === FALLBACK_LOCALE) {
      state.locale = initial;
      applyStaticTranslations();
    } else {
      setLocale(initial, { persist: false });
    }
  },
};

/** Reactive, read-only view of the engine state. */
export const i18nState = readonly(state);

/** Convenience computed for components that only need the active code. */
export const currentLocale = computed(() => state.locale);

export { state };
export default i18n;
